import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { SelectField } from "components/AddVaccineForm/components";
import { FormField } from "components/form/FormField/FormField";

import style from "../AddVaccineForm.module.css";

/**
 * A select that stops pretending to be one when the product leaves a single answer.
 *
 * Several fields here are properties of the vaccine rather than decisions: nirsevimab is
 * intramuscular, a single-dose course has one dose number. Rendering those as a dropdown
 * invites the GP to open it, read one line and close it again — and implies something
 * else might be selectable. Shown as a filled, read-only input instead, with no asterisk:
 * nothing is being asked, so marking it required is a demand they cannot act on.
 *
 * Local to the vaccine form on purpose. The shared SelectField is also used by the
 * patient profile, baseline and DEASP forms, where a list narrowed to one entry — or one
 * still loading — must not silently lock a value into form state.
 */
export const LockedOrSelectField = ({ name, label, options, ...selectProps }) => {
  const { watch, setValue } = useFormContext();
  const selectableOptions = options.filter((option) => option.enabled !== false);
  const onlyOption = selectableOptions.length === 1 ? selectableOptions[0] : null;
  const currentValue = watch(name);

  // The read-only input registers nothing, so the value is written to form state here.
  // Callers that already set it — RSV's route effect, for instance — write the same
  // value, so this is a guarantee rather than a competing source of truth.
  useEffect(() => {
    if (onlyOption && currentValue !== onlyOption.value) {
      setValue(name, onlyOption.value, { shouldValidate: true });
    }
  }, [onlyOption, currentValue, name, setValue]);

  if (!onlyOption) {
    return (
      <SelectField name={name} label={label} options={options} {...selectProps} />
    );
  }

  return (
    <FormField name={name}>
      <label className={style.pregnancyFormLabel}>{label}</label>
      <input
        type="text"
        className="form-control"
        value={onlyOption.label}
        disabled
      />
    </FormField>
  );
};
